import { Howl } from 'howler';
import { setCorrectingInterval, clearCorrectingInterval } from './correctingInterval';
import Board from './types/Board';
import Rules from './types/Rules';
import Config from './types/Config';
import Player from './types/Player';
import GameState from './types/GameState';
import PlayerInfo from './types/PlayerInfo';

class BasicGame implements GameState {
  ['constructor']!: typeof BasicGame // ! to avoid 'use before init' error in constructor

  timeInterval = 42;

  startSound: object;

  gameOverSound: object;

  moveSound: object;

  captureSound: object;

  public readonly id?: string;

  players: Player[];

  createdAt: Date;

  duration: number;

  increment: number;

  mode: string;

  type: string;

  sides: string[];

  sideNames: object;

  result: string;

  finished: boolean;

  winner: string;

  turn: number;

  turnStart: number;

  rematch: object;

  config: Config;

  playerInfo: PlayerInfo[];

  moves: string[];

  boardHistory: Array<any>;

  board: Board;

  lastInterval: any;

  rules: Rules;

  constructor(obj: GameState) {
    if (obj.the_game) {
      obj = this.constructor.deserialize(obj.the_game);
    }
    const or = this.constructor.orFn;
    // this.lastInterval = or(obj.lastInterval, null);
    this.startSound = this.constructor.loadSound('notify', 0.1);
    this.gameOverSound = this.constructor.loadSound('defeat', 0.1);
    this.moveSound = this.constructor.loadSound('move');
    this.captureSound = this.constructor.loadSound('capture');
    this.id = obj.id;
    this.players = obj.players;
    this.createdAt = obj.createdAt;
    this.duration = or(obj.duration, 60 * obj.min);
    this.increment = or(obj.increment, obj.inc);
    this.mode = obj.mode;
    this.type = obj.type;
    this.sides = obj.sides || ['b', 'w'];
    this.moves = obj.moves || [];
    this.sideNames = obj.sideNames || { b: 'Black', a: 'White' };
    this.result = obj.result || '';
    this.finished = or(obj.finished, false);
    this.winner = or(obj.winner, null);
    this.turn = or(obj.turn, 1);
    this.turnStart = or(obj.turnStart, null);
    this.rematch = or(obj.rematch, null);
    this.config = obj.config;
    this.playerInfo = obj.playerInfo || obj.config.playerInfo;
    for (let i = 0; i < this.playerInfo.length; i += 1) {
      if (obj && obj.playerInfo && obj.playerInfo[i] && typeof obj.playerInfo[i].actualTimeLeft !== 'undefined') {
        this.playerInfo[i].actualTimeLeft = obj.playerInfo[i].actualTimeLeft;
      } else {
        this.playerInfo[i].actualTimeLeft = this.duration * 1000;
      }

      if (obj && obj.playerInfo && obj.playerInfo[i] && typeof obj.playerInfo[i].timeLeft !== 'undefined') {
        this.playerInfo[i].timeLeft = obj.playerInfo[i].timeLeft;
      } else {
        this.playerInfo[i].timeLeft = this.duration * 1000;
      }

      if (obj && obj.playerInfo && obj.playerInfo[i] && typeof obj.playerInfo[i].clockStarted !== 'undefined') {
        this.playerInfo[i].clockStarted = obj.playerInfo[i].clockStarted;
      } else {
        this.playerInfo[i].clockStarted = false;
      }
    }

    if (this.turnStart != null) {
      this.lastInterval = setCorrectingInterval(() => {
        const tpi = this.getTurnPlayerInfo();
        if (this.turnStart != null && tpi?.clockStarted && tpi.timeLeft && this.result === '') {
          tpi.timeLeft -= this.timeInterval;
          if (tpi.timeLeft < 0) {
            const winner = this.getNextTurnPlayerSide();
            let result = 'Time is up. ';
            result += `${this.sideNames[winner]} won.`;
            this.gameOver(winner, null, result);
            clearCorrectingInterval(this.lastInterval);
          }
        }
      }, this.timeInterval);
    }
  }

  static loadSound(soundName: string, volume = 1) {
    return new Howl({ src: [`/${soundName}.wav`], volume });
  }

  playSound(captured: number) {
    if (this.turn === 1) {
      this.startSound.play();
    } else if (this.result !== '') {
      this.gameOverSound.play();
    } else if (captured === 0) {
      this.moveSound.play();
    } else {
      this.captureSound.play();
    }
  }

  static deserialize(serialized: string): GameState {
    return JSON.parse(serialized);
  }

  static orFn(obj, alt) {
    return (typeof obj !== 'undefined') ? obj : alt;
  }

  serialize() {
    const {
      startSound,
      gameOverSound,
      moveSound,
      captureSound,
      ...that
    } = this;

    return JSON.stringify(that);
  }

  addMove(notation: string): void {
    this.moves.push(notation);
    // this.addBoardToHistory();
  }

  addBoardToHistory() {
    this.boardHistory.push(JSON.parse(JSON.stringify(this.board)));
  }

  gameOver(side: string, notation: string, result: string) {
    this.finished = true;
    this.winner = side;
    // if (notation != null) {
    this.addMove(notation);
    // }
    this.result = result;
  }

  getPlayer(uid: string) {
    return this.players.find(p => p.uid === uid);
  }

  getPlayerInfoBySide(side: string) {
    return this.playerInfo.find(p => p.side === side);
  }

  getTurnPlayerSide() {
    return this.sides[this.turn % 2];
  }

  getNextTurnPlayerSide() {
    return this.sides[(this.turn + 1) % 2];
  }

  getTurnPlayerInfo() {
    return this.getPlayerInfoBySide(this.getTurnPlayerSide());
  }

  beforeTurnChange() {
    if (this.turnStart != null && this.getTurnPlayerInfo().clockStarted) {
      const timeSpent = Date.now() - this.turnStart;
      this.getTurnPlayerInfo().actualTimeLeft += this.increment * 1000;
      this.getTurnPlayerInfo().actualTimeLeft -= timeSpent;
      this.getTurnPlayerInfo().actualTimeLeft += 800; // this is to discount network effect
      this.getTurnPlayerInfo().timeLeft = this.getTurnPlayerInfo().actualTimeLeft;
    }

    this.turnStart = Date.now();
  }
}

export default BasicGame;
